import { ReactNode, FC, ComponentProps } from 'react'

import { RadioGroup as HeadlessRadioGroup } from '@headlessui/react'
import { clsx } from 'clsx'

import { HTMLTribeProps } from '../types/index.js'
import { runIfFn } from '../utils/index.js'
import { Radio } from './Radio.js'
import {
  RadioGroupsProvider,
  RadioGroupVariant,
  useRadioGroups,
} from './RadioGroupContext.js'

interface RadioGroupRenderProps {
  active: boolean
  selected: boolean
  disabled: boolean
}

export type RadioGroupProps<T = string> = Omit<
  HTMLTribeProps<'div'>,
  'onChange'
> & {
  value: T
  onChange: (newValue: T) => void
  children?: ReactNode | ((props: RadioGroupRenderProps) => ReactNode)
  disabled?: boolean
  variant?: RadioGroupVariant
  name?: string
}
/**
 * Radios are used when only one choice may be selected in a series of options.
 */
export const RadioGroup = ({
  children,
  variant = 'list',
  ...rest
}: RadioGroupProps) => {
  return (
    <HeadlessRadioGroup {...rest}>
      <div className="relative isolate">
        <RadioGroupsProvider variant={variant}>{children}</RadioGroupsProvider>
      </div>
    </HeadlessRadioGroup>
  )
}

export type RadioGroupItemsProps = ComponentProps<'div'>

const RadioGroupItems: FC<RadioGroupItemsProps> = props => {
  const { children, className, ...rest } = props

  const { variant } = useRadioGroups()
  const variantClsx = [
    variant === 'list' && 'rounded-md -space-y-px',
    variant === 'stacked-cards' && 'space-y-4',
  ]

  return (
    <div className={clsx(variantClsx, className)} {...rest}>
      {children}
    </div>
  )
}

const RadioGroupItemsMinimal: FC<RadioGroupItemsProps> = props => {
  const { children, className, ...rest } = props

  return (
    <div className={clsx('space-y-1', className)} {...rest}>
      {children}
    </div>
  )
}

interface RadioGroupItemRenderProps {
  checked: boolean
}
export type RadioGroupItemProps<T = any> = Omit<
  HTMLTribeProps<'div'>,
  'title' | 'children'
> & {
  value: T
  disabled?: boolean
  title?: string | ReactNode
  description?: string | ReactNode
  children?: ReactNode | ((props: RadioGroupItemRenderProps) => ReactNode)
}

const RadioGroupItem: FC<RadioGroupItemProps> = props => {
  const { variant } = useRadioGroups()

  switch (variant) {
    case 'list':
    default:
      return <RadioGroupItemList {...props} />
    case 'stacked-cards':
      return <RadioGroupItemCard {...props} />
  }
}

const RadioGroupItemList: FC<RadioGroupItemProps> = props => {
  const { value, title, description, children, disabled, className, ...rest } =
    props

  return (
    <HeadlessRadioGroup.Option
      as="label"
      className={renderProps =>
        clsx(
          renderProps.checked
            ? 'bg-surface-pressed text-content border-focused z-10'
            : 'bg-surface border-line',
          'relative border p-4 flex',
          'focus:outline-none  focus-visible:ring',
          'first:rounded-ts-md first:rounded-te-md',
          'last:rounded-bs-md last:rounded-be-md',
          !renderProps.disabled && ' cursor-pointer',
          renderProps.disabled && 'opacity-60 cursor-default',
          className,
        )
      }
      value={value}
      disabled={disabled}
      {...rest}
    >
      {renderProps => (
        <Radio
          active={renderProps.active}
          checked={renderProps.checked}
          disabled={renderProps.disabled}
        >
          {title ? (
            <>
              <HeadlessRadioGroup.Label
                as="span"
                className="text-content block text-sm font-medium"
              >
                {title}
              </HeadlessRadioGroup.Label>
              {description && (
                <HeadlessRadioGroup.Description
                  as="span"
                  className="text-content block text-sm"
                >
                  {description}
                </HeadlessRadioGroup.Description>
              )}
            </>
          ) : (
            runIfFn(children, renderProps)
          )}
        </Radio>
      )}
    </HeadlessRadioGroup.Option>
  )
}

const RadioGroupItemMinimal: FC<RadioGroupItemProps> = props => {
  const { value, title, children, disabled, className, ...rest } = props

  return (
    <HeadlessRadioGroup.Option
      as="label"
      className={renderProps =>
        clsx(
          renderProps.checked
            ? 'text-content z-10'
            : 'bg-surface hover:bg-surface-hovered',
          'h-11 flex p-2 items-center rounded-md ',
          'focus:outline-none  focus-visible:ring',
          !renderProps.disabled && ' cursor-pointer',
          renderProps.disabled && 'opacity-60 cursor-default',
          className,
        )
      }
      value={value}
      disabled={disabled}
      {...rest}
    >
      {renderProps => (
        <Radio
          active={renderProps.active}
          checked={renderProps.checked}
          disabled={renderProps.disabled}
        >
          {title ? (
            <>
              <HeadlessRadioGroup.Label
                as="span"
                className="text-content block text-sm font-medium"
              >
                {title}
              </HeadlessRadioGroup.Label>
            </>
          ) : (
            runIfFn(children, renderProps)
          )}
        </Radio>
      )}
    </HeadlessRadioGroup.Option>
  )
}

const RadioGroupItemCard: FC<RadioGroupItemProps> = props => {
  const { value, children, disabled, className, ...rest } = props

  return (
    <HeadlessRadioGroup.Option
      as="label"
      className={renderProps =>
        clsx(
          renderProps.active ? 'ring-1 ring-focused' : '',
          'relative block rounded-lg border border-line shadow-sm px-6 py-4 sm:flex sm:justify-between',
          ' bg-surface text-content-subdued',
          'focus:outline-none focus-visible:ring',
          !renderProps.disabled && 'cursor-pointer hover:border-line',
          renderProps.disabled && 'opacity-60 cursor-default',
          className,
        )
      }
      value={value}
      disabled={disabled}
      {...rest}
    >
      {renderProps => <>{runIfFn(children, renderProps)}</>}
    </HeadlessRadioGroup.Option>
  )
}

RadioGroup.Items = RadioGroupItems
RadioGroup.ItemsMinimal = RadioGroupItemsMinimal
RadioGroup.Item = RadioGroupItem
RadioGroup.ItemMinimal = RadioGroupItemMinimal
